﻿import { useState, useCallback, useRef } from 'react';
import { api } from '@/services/api';
import { config } from '@/config/env';
import { getSettoriObbligatori } from '@/utils/obbligoSettori';
import { ApiError, extractApiErrorMessage } from '@/services/api';

export interface HaskQuestion {
  codice: string;
  codgruppo: string;
  codattr: string;
  domanda: string;
  tipo: 'SELECT' | 'MULTISELECT' | 'TEXT' | 'TEXTAREA' | 'DATE' | 'CHECK' | 'NUM' | 'RADIOIMAGE' | 'IMAGE';
  required: 'S' | 'N';
  readonly: 'S' | 'N';
  visibile: 'S' | 'N';
  colspan: string;
  dependson: string | null;
  valore_predefinito: string | null;
  help: string | null;
  azione: string | null;
  metodovalidazione: string | null;
  critica: string | null;
  risposte: HaskAnswer[];
  validazione: { minlength: number; maxlength: number; valmin: number; valmax: number } | null;
}

export interface HaskAnswer {
  codice: string;
  valore: string;
  codpadre: string | null;
  etichetta: string | null;
  nextgruppo: string | null;
}

export interface HaskHeader {
  codquestionario: string;
  tipo: string;
}

export function useHask() {
  const [questions, setQuestions] = useState<HaskQuestion[]>([]);
  const questionsCache = useRef<Map<string, HaskQuestion[]>>(new Map());
  const [codquestionario, setCodquestionario] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [answersVersion, setAnswersVersion] = useState(0);
  const answersRef = useRef<Record<string, string>>({});

  const getLang = () => sessionStorage.getItem('lang') || config.LANG.trim() || 'it';

  const getHeader = useCallback(async (area: string, codice: string, categoria = '*') => {
    try {
      const data = await api.post<HaskHeader>(
        'lmeet/questionario/testata/' + config.SITE + '/' + getLang(),
        { area: area.toUpperCase(), categoria: categoria.toUpperCase(), codice: codice.toUpperCase() }
      );
      return data;
    } catch { return null; }
  }, []);

  const loadQuestions = useCallback(async (area: string, codice: string, categoria = '*') => {
    setLoading(true);
    try {
      const header = await getHeader(area, codice, categoria);
      if (!header || !header.codquestionario) {
        setQuestions([]);
        setLoading(false);
        return;
      }

      setCodquestionario(header.codquestionario);

      const cacheKey = header.codquestionario + '_' + area + '_' + codice;
      let data: HaskQuestion[] | null = questionsCache.current.get(cacheKey) || null;
      
      if (!data) {
        data = await api.post<HaskQuestion[]>(
          'lmeet/questionario/domande/' + config.SITE + '/' + getLang(),
          {
            codquestionario: header.codquestionario,
            area,
            codice,
            categoria,
            mapRisposte: answersRef.current,
          }
        );
        if (data) questionsCache.current.set(cacheKey, data);
      }

      // Normalize: filter dependson questions
      const normalized = normalizeQuestions(data || [], answersRef.current);
      setQuestions(normalized);
    } catch {
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  }, [getHeader]);

  const normalizeQuestions = (qs: HaskQuestion[], answers: Record<string, string>): HaskQuestion[] => {
    let result = [...qs];
    const dependsonList = result.filter(q => q.dependson && q.dependson.trim() !== '');

    for (const obj of dependsonList) {
      const parent = result.find(q => q.codice === obj.dependson);
      if (!parent) continue;

      const val = answers[parent.codattr] || '';
      if (val === '') {
        // Keep only first question that depends on this parent
        const first = result.find(d => d.dependson === parent.codice);
        if (first && first.codice !== obj.codice) {
          result = result.filter(d => d.codice !== obj.codice);
        }
      } else {
        const answer = parent.risposte?.find(a => a.codice === val);
        if (answer && answer.nextgruppo !== obj.codgruppo) {
          result = result.filter(d => d.codice !== obj.codice);
        }
      }
    }
    return result;
  };

  const setAnswer = useCallback((codattr: string, value: string) => {
    answersRef.current[codattr] = value;
    setAnswersVersion(v => v + 1);
  }, []);

  const getAnswers = useCallback(() => ({ ...answersRef.current }), []);

  /**
   * Valida il questionario. Oltre all'obbligatorietà statica (required === 'S'),
   * applica l'obbligatorietà condizionale dei Settori LS/ITC in funzione
   * dell'Azienda LSG selezionata (LME-54).
   * Restituisce la prima domanda obbligatoria non compilata (o null se valido),
   * così da poter mostrare un messaggio d'errore specifico.
   */
  const validateDetailed = useCallback((): { valid: boolean; missing: HaskQuestion | null } => {
    const settoriObbligatori = getSettoriObbligatori(questions, answersRef.current);
    let missing: HaskQuestion | null = null;

    for (const q of questions) {
      if (q.visibile === 'N') continue;
      const required = q.required === 'S' || settoriObbligatori.has(q.codattr);
      if (!required) continue;
      const val = answersRef.current[q.codattr] || '';
      if (val === '') { missing = q; break; }
    }

    const valid = missing === null;
    setIsValid(valid);
    return { valid, missing };
  }, [questions]);

  const validate = useCallback((): boolean => validateDetailed().valid, [validateDetailed]);

  /**
   * Costruisce la mappa completa delle risposte da inviare al backend.
   * Come nel vanilla (getFormHaskValues), il payload deve contenere TUTTE le
   * domande del questionario (per codattr), non solo quelle valorizzate: le
   * domande non compilate vengono inviate con valore vuoto (o 'N' per i CHECK).
   * Inviare una mappa parziale fa fallire il salvataggio lato backend (HTTP 500).
   */
  const buildCompleteRisposte = useCallback((): Record<string, string> => {
    const answers = answersRef.current;
    const risposte: Record<string, string> = {};
    for (const q of questions) {
      const val = answers[q.codattr];
      if (val !== undefined && val !== null) {
        risposte[q.codattr] = val;
      } else if (q.tipo === 'CHECK') {
        risposte[q.codattr] = 'N';
      } else {
        risposte[q.codattr] = q.valore_predefinito ?? '';
      }
    }
    return risposte;
  }, [questions]);

  const save = useCallback(async (codazienda: string, codcliente: string, files: File[] = [], visitId?: string) => {
    const risposte = buildCompleteRisposte();
    const inputws: Record<string, unknown> = {
      codazienda,
      codcliente,
      tipologia: 'VISITA',
      codquestionario,
      risposte,
      attributi: { area: 'VISITA', codice: codazienda.toUpperCase(), categoria: '*' },
    };
    if (visitId) inputws.id = visitId;

    if (files.length > 0) {
      const formData = new FormData();
      formData.append('qstrequest', JSON.stringify(inputws));
      files.forEach(f => formData.append('attachments', f));

      const token = sessionStorage.getItem('mT');
      const res = await fetch(
        (config.WSREST_TABATA.replace(/\/+$/, '')) + '/lmeet/risposte-questionario/save-with-attachments/v2',
        { method: 'POST', headers: { Authorization: 'Bearer ' + token }, body: formData }
      );
      if (!res.ok) {
        // Estrae il messaggio d'errore dal body del backend e lo propaga con lo status.
        throw new ApiError(await extractApiErrorMessage(res), res.status);
      }
      return await res.json();
    }

    return await api.post('lmeet/risposte-questionario/save', inputws);
  }, [codquestionario, buildCompleteRisposte]);

  const resetAnswers = useCallback(() => {
    answersRef.current = {};
    setQuestions([]);
    setCodquestionario('');
    setIsValid(false);
    setAnswersVersion(0);
  }, []);

  return {
    questions, codquestionario, loading, isValid, answersVersion,
    loadQuestions, setAnswer, getAnswers, validate, validateDetailed, save, resetAnswers,
  };
}

